import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { IconButton } from './icon-button'

// Bottom sheet — the mobile pattern for filters, pickers and quick actions
// (thumb-reachable, swipe-down to dismiss). Portalled over the app; a backdrop
// tap, Esc, the close button, or a downward drag past a threshold all dismiss —
// the gesture always has a visible fallback (grabber + close). The enter slide is
// gated behind prefers-reduced-motion; the panel is width-capped to the app
// column so it never stretches on a tablet.
export function BottomSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title?: string | undefined
  children: ReactNode
}) {
  const [dragY, setDragY] = useState(0)
  const [dragging, setDragging] = useState(false)
  const startY = useRef<number | null>(null)

  // Esc to close + lock the background scroll while the sheet is open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  // Clear any residual drag offset when the sheet (re)opens.
  useEffect(() => {
    if (open) setDragY(0)
  }, [open])

  if (!open) return null

  function onTouchStart(e: React.TouchEvent) {
    const touch = e.touches[0]
    if (!touch) return
    startY.current = touch.clientY
    setDragging(true)
  }
  function onTouchMove(e: React.TouchEvent) {
    const touch = e.touches[0]
    if (touch == null || startY.current === null) return
    const dy = touch.clientY - startY.current
    if (dy > 0) setDragY(dy) // track downward drag only
  }
  function onTouchEnd() {
    setDragging(false)
    if (dragY > 96) onClose()
    else setDragY(0)
    startY.current = null
  }

  return createPortal(
    <div className="fixed inset-0 z-[var(--z-modal)] flex items-end justify-center">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 um-fade"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: dragY ? `translateY(${dragY}px)` : undefined }}
        className={`um-sheet relative w-full max-w-screen-sm max-h-[85dvh] overflow-y-auto rounded-t-[var(--radius-card)] bg-[var(--surface-card)] border-t border-[var(--line)] safe-bottom ${
          dragging ? '' : 'transition-transform duration-200'
        }`}
      >
        {/* grabber — the swipe-down affordance */}
        <div className="sticky top-0 z-10 pt-2 pb-1 flex justify-center bg-[var(--surface-card)]">
          <span className="h-1 w-10 rounded-full bg-[var(--line-strong)]" />
        </div>
        {title && (
          <div className="px-4 pb-2 flex items-center justify-between gap-2">
            <h2 className="font-bold text-[length:var(--fs-lg)] truncate">{title}</h2>
            <IconButton label="Cerrar" onClick={onClose} className="-mr-2 shrink-0">
              <X size={20} />
            </IconButton>
          </div>
        )}
        <div className="px-4 pb-3">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
